import { Light, LightFields, LightProperties } from '../../core/Light';
import { Vector3, Euler } from 'three';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { colors } from '@/constants/theme';
import { Field } from '@/types/core/Field';

export interface HemisphereLightProperties extends LightProperties {
  intensity: number;
  color: string;
  groundColor: string;
}

// Create a reusable Hemisphere Light component that can be used in both preview and world
const HemisphereLightComponent = forwardRef<THREE.HemisphereLight, { properties: HemisphereLightProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { intensity, color, groundColor } = properties;
  const localRef = useRef<THREE.HemisphereLight>(null);

  // Forward the ref to parent
  useImperativeHandle(ref, () => localRef.current!, []);

  return (
    <>
      <hemisphereLight
        ref={localRef}
        intensity={intensity}
        color={color}
        groundColor={groundColor}
        position={[0, 1, 0]}
      />
      {isPreview && (
        <>
          {/* Ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color={colors.contrast} />
          </mesh>
          
          {/* Demo objects to show hemisphere lighting */}
          <mesh position={[-1.5, 0, 0]}>
            <boxGeometry args={[1, 1, 1]} />
            <meshStandardMaterial color={colors.primary} />
          </mesh>
          
          <mesh position={[1.5, 0, 0]}>
            <sphereGeometry args={[0.6, 32, 32]} />
            <meshStandardMaterial color={colors.muted} />
          </mesh>
        </>
      )}
    </>
  );
});

export const HemisphereLightItem: Light<HemisphereLightProperties> = {
  id: '',
  name: 'Hemisphere Light',
  type: 'light',
  subtype: 'hemisphere',
  icon: 'Globe',
  properties: {
    intensity: 1,
    color: '#ffffff', // Sky color
    groundColor: '#444444', // Ground color
    transform: {
      position: new Vector3(0, 0, 0),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1),
    },
  },
  fields: [
    ...LightFields,
    {
      key: 'properties.groundColor',
      type: 'color',
      label: 'Ground Color',
      default: '#444444',
      panel: 'light',
    },
    // Hemisphere light doesn't need transform fields as it affects the entire scene from above
  ],
  render: (properties: HemisphereLightProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <HemisphereLightComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.HemisphereLight>} />;
  }
};